import React, { useState, useEffect } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, Building2, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);
  const [settings, setSettings] = useState({
    default_labour_rate: 45,
    overhead_pct: 10,
    contingency_pct: 5,
    target_margin_pct: 15,
    target_margin_min: null,
    target_margin_max: null,
    overhead_method: 'revenue_based'
  });

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await api.get('/organisations/current');
        setOrg(res.data);
        if (res.data.organisation?.settings) {
          setSettings(res.data.organisation.settings);
        }
      } catch (error) {
        console.error('Failed to fetch org', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrg();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Compute midpoint for backward compatibility
      const min = settings.target_margin_min ?? (settings.target_margin_pct - 2);
      const max = settings.target_margin_max ?? (settings.target_margin_pct + 2);
      const payload = {
        ...settings,
        target_margin_min: min,
        target_margin_max: max,
        target_margin_pct: (min + max) / 2,
      };
      await api.put('/organisations/settings', payload);
      setSettings(payload);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AppLayout>
    );
  }

  const { organisation, trade_config, country_config } = org || {};
  const currencySymbol = country_config?.currency_symbol || '£';

  return (
    <AppLayout>
      <div className="space-y-6 max-w-2xl" data-testid="settings-container">
        {/* Header */}
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="text-slate-500">Configure your organisation defaults</p>
        </div>

        {/* Organisation Info */}
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="section-title text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Organisation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Company Name</Label>
                <p className="font-medium text-slate-900">{organisation?.name}</p>
              </div>
              <div>
                <Label className="text-slate-500">Country</Label>
                <p className="font-medium text-slate-900">
                  {country_config?.display_name} ({country_config?.currency})
                </p>
              </div>
              <div>
                <Label className="text-slate-500">Trade</Label>
                <p className="font-medium text-slate-900">{trade_config?.display_name}</p>
              </div>
              <div>
                <Label className="text-slate-500">Typical Margin Range</Label>
                <p className="font-medium text-slate-900">
                  {trade_config?.typical_margin_range?.min}% - {trade_config?.typical_margin_range?.max}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Default Settings */}
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="section-title text-lg flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              Default Values
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="labour_rate">Default Labour Rate ({currencySymbol}/hr)</Label>
                <Input
                  id="labour_rate"
                  type="number"
                  value={settings.default_labour_rate}
                  onChange={(e) => setSettings({ ...settings, default_labour_rate: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="settings-labour-rate"
                />
              </div>
              
              <div>
                <Label htmlFor="target_margin_min">Target Margin Band (%)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="target_margin_min"
                    type="number"
                    step="0.5"
                    value={settings.target_margin_min ?? (settings.target_margin_pct - 2)}
                    onChange={(e) => setSettings({ ...settings, target_margin_min: parseFloat(e.target.value) || 0 })}
                    className="w-24"
                    data-testid="settings-target-margin-min"
                  />
                  <span className="text-slate-400 text-sm">to</span>
                  <Input
                    id="target_margin_max"
                    type="number"
                    step="0.5"
                    value={settings.target_margin_max ?? (settings.target_margin_pct + 2)}
                    onChange={(e) => setSettings({ ...settings, target_margin_max: parseFloat(e.target.value) || 0 })}
                    className="w-24"
                    data-testid="settings-target-margin-max"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Bids below the minimum trigger margin warnings
                </p>
              </div>
              
              <div>
                <Label htmlFor="overhead">Overhead (%)</Label>
                <Input
                  id="overhead"
                  type="number"
                  value={settings.overhead_pct}
                  onChange={(e) => setSettings({ ...settings, overhead_pct: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="settings-overhead"
                />
              </div>
              
              <div>
                <Label htmlFor="contingency">Contingency (%)</Label>
                <Input
                  id="contingency"
                  type="number"
                  value={settings.contingency_pct}
                  onChange={(e) => setSettings({ ...settings, contingency_pct: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="settings-contingency"
                />
              </div>
            </div>
            
            <div>
              <Label>Overhead Calculation Method</Label>
              <Select
                value={settings.overhead_method}
                onValueChange={(v) => setSettings({ ...settings, overhead_method: v })}
              >
                <SelectTrigger className="mt-2" data-testid="settings-overhead-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue_based">Revenue-based (% of bid price)</SelectItem>
                  <SelectItem value="cost_based">Cost-based (% of direct costs)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Revenue-based is the default for most subcontractors
              </p>
            </div>
            
            <Button
              onClick={handleSave}
              className="btn-primary"
              disabled={saving}
              data-testid="settings-save-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Regulatory Notes */}
        {country_config?.regulatory_notes?.length > 0 && (
          <Card className="card-industrial bg-slate-50">
            <CardHeader>
              <CardTitle className="section-title text-lg">
                {country_config.display_name} Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-slate-600">
                {country_config.regulatory_notes.map((note, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-orange-500">•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
